import { useEffect, useState } from "react";
import { useParams } from "react-router-dom";
import { api } from "@/lib/api";
import { Button } from "@/components/ui/button";
import { PrinterIcon, ArrowLeftIcon } from "@phosphor-icons/react";

const inr = (n) => "₹" + Number(n || 0).toLocaleString("en-IN", { maximumFractionDigits: 2 });

/**
 * Printable event bill for a Halls booking. Uses the same layout/config as the
 * reservation invoice (branded banner + section blocks) so guests always
 * receive an on-brand document regardless of event vs stay.
 */
export default function HallBookingPrint() {
  const { id } = useParams();
  const [booking, setBooking] = useState(null);
  const [hall, setHall] = useState(null);
  const [invoice, setInvoice] = useState(null);
  const [property, setProperty] = useState(null);
  const [platform, setPlatform] = useState({});

  useEffect(() => {
    (async () => {
      // Always try every property until we locate the booking — we don't know
      // its property up front and /halls/bookings is property-scoped.
      let found = null;
      let ownerProp = null;
      try {
        const props = await api.get("/properties");
        for (const p of props.data || []) {
          const rows = await api.get("/halls/bookings", { params: { property_id: p.id } }).catch(() => ({ data: [] }));
          const b = (rows.data || []).find((x) => x.id === id);
          if (b) { found = b; ownerProp = p; break; }
        }
      } catch {}
      if (found) {
        setBooking(found);
        setProperty(ownerProp);
        // Invoice + hall are optional — no invoice yet for an un-billed booking
        api.get(`/halls/bookings/${id}/invoice`).then((r) => setInvoice(r.data)).catch(() => {});
        api.get("/halls", { params: { property_id: ownerProp.id } }).then((r) => setHall((r.data || []).find((h) => h.id === found.hall_id))).catch(() => {});
      }
      api.get("/settings/platform/public").then((r) => setPlatform(r.data || {})).catch(() => {});
    })();
  }, [id]);

  if (!booking) return <div className="min-h-screen flex items-center justify-center text-muted-foreground">Loading event bill…</div>;

  const L = { ...(platform.invoice_layout || {}) };
  const banner = L.show_header_banner !== false;
  const displayName = (L.header_property_display || "").trim() || property?.name || booking?.property_id;
  const overline = (L.header_group_overline || "").trim() || platform.group_name || "The King's Grand Group of Hotels";
  const location = (L.header_location_badge || "").trim() || [property?.city, property?.state].filter(Boolean).join(", ");
  const bg = L.header_bg_color || "#2F5D50";
  const accent = L.header_accent_color || "#C8A24B";
  const nights = Math.max(1, Math.round(((new Date(booking.end_at) - new Date(booking.start_at)) / (1000 * 60 * 60)) * 10) / 10);

  const total = invoice?.total || booking.total_amount || 0;
  const deposit = invoice?.deposit_applied || booking.deposit_amount || 0;
  const balance = Math.max(0, total - deposit);

  return (
    <div className="min-h-screen bg-background text-foreground py-8 print:py-0">
      <div className="max-w-3xl mx-auto px-4 print:px-0 print:max-w-none">
        <div className="flex items-center justify-between gap-4 mb-4 print:hidden">
          <Button variant="outline" size="sm" onClick={() => window.history.back()}><ArrowLeftIcon size={14} className="mr-1" /> Back</Button>
          <Button size="sm" onClick={() => window.print()} data-testid="hall-print-btn"><PrinterIcon size={14} className="mr-1" /> Print</Button>
        </div>

        <div className="rounded-md overflow-hidden border border-border">
          {banner && (
            <div className="p-6 text-white" style={{ backgroundColor: bg }} data-testid="hall-print-banner">
              <p className="text-xs uppercase tracking-[0.25em] mb-2" style={{ color: accent }}>◆ {overline}</p>
              <h1 className="font-display text-3xl md:text-4xl leading-tight" style={{ color: accent }}>
                {displayName}
                {location && <span className="ml-3 align-middle inline-block text-[10px] tracking-widest uppercase border rounded-full px-2 py-1" style={{ borderColor: accent, color: "white" }}>{location}</span>}
              </h1>
              <p className="text-sm mt-2 opacity-90">Event bill · {booking.booking_ref}</p>
            </div>
          )}
          <div className="p-6 bg-card space-y-6">
            <div className="grid grid-cols-2 gap-6">
              <div>
                <p className="label-overline mb-1">Client</p>
                <p className="font-medium">{booking.customer_name}</p>
                <p className="text-sm text-muted-foreground">{booking.customer_phone || "—"}</p>
              </div>
              <div>
                <p className="label-overline mb-1">Event</p>
                <p className="font-medium">{hall?.name || "—"} · {booking.purpose}</p>
                <p className="text-sm text-muted-foreground">{new Date(booking.start_at).toLocaleString("en-IN")} → {new Date(booking.end_at).toLocaleString("en-IN")}</p>
                <p className="text-xs text-muted-foreground">Guests: {booking.guest_count} · {nights} hr(s)</p>
              </div>
            </div>

            <div>
              <p className="label-overline mb-2">Charges</p>
              <table className="w-full text-sm">
                <tbody>
                  {(invoice?.line_items || [{ description: hall?.name || "Hall rental", amount: booking.total_amount || 0 }]).map((li, i) => (
                    <tr key={i} className="border-b border-border last:border-b-0">
                      <td className="py-2">{li.description}</td>
                      <td className="py-2 text-right font-mono-plex">{inr(li.amount)}</td>
                    </tr>
                  ))}
                </tbody>
              </table>
            </div>

            <div className="ml-auto max-w-sm space-y-1">
              <div className="flex justify-between text-sm"><span>Subtotal</span><span className="font-mono-plex">{inr(invoice?.subtotal || total)}</span></div>
              {invoice?.tax_amount ? <div className="flex justify-between text-sm"><span>GST</span><span className="font-mono-plex">{inr(invoice.tax_amount)}</span></div> : null}
              <div className="flex justify-between pt-2 border-t border-border font-medium">
                <span>Grand Total</span><span className="font-display text-xl">{inr(total)}</span>
              </div>
              {deposit > 0 && <div className="flex justify-between text-[hsl(var(--success))] text-sm"><span>Less: Deposit</span><span className="font-mono-plex">− {inr(deposit)}</span></div>}
              {(balance > 0 || deposit > 0) && (
                <div className="flex justify-between pt-2 border-t border-border font-medium">
                  <span>Balance due</span><span className="font-display text-lg" data-testid="hall-print-balance">{inr(balance)}</span>
                </div>
              )}
            </div>

            {booking.package_notes && (
              <div className="pt-4 border-t border-border">
                <p className="label-overline mb-1">Notes</p>
                <p className="text-sm whitespace-pre-line">{booking.package_notes}</p>
              </div>
            )}

            <div className="pt-6 border-t border-border text-center text-xs text-muted-foreground">
              <p>Thank you for celebrating with {displayName}.</p>
              <p className="mt-1">This is a computer-generated event bill.</p>
            </div>
          </div>
        </div>
      </div>
    </div>
  );
}
